import ko from 'knockout';
import Backbone from 'backbone';
import { keyCode } from '@bingads-webui/keycode';
import template from '../template/index.pug';
import ViewModel from '../viewmodel/index';

/**
 * @this {SearchBox}
 * @private
 * @returns {void} void
 */
function search() {
  const value = this.viewModel.value();

  this.trigger('search', value);
}

/**
 * @this {SearchBox}
 * @private
 * @param {Object} event - event object
 * @returns {void} void
 */
function onClick(event) {
  event.preventDefault();
  search.call(this);
}

/**
 * @this {SearchBox}
 * @private
 * @param {Object} event - event object
 * @returns {void} void
 */
function onKeyPress(event) {
  if (event.keyCode === keyCode.Enter) {
    event.preventDefault();
    search.call(this);
  }
}

/**
 * View for search box component with an input box and a search icon
 * @param {Object} options - the view options
 * @param {Number} [options.maxLength=150] - the maximum length of the search text
 * @param {String} [options.placeholder=''] - the placeholder for the search box
 * @param {String} [options.value=''] - default value of the input box
 * @param {boolean} [options.isDisabled=false] - disable the input box or not
 * @param {Array<String>} [options.iconClasses=[]] - the classes to assign to the icon's
 * span element. This is helpful if glyphicon is to be used. Then two classes must be
 * passed: class of the icon, and genereal glyphicon class.
 * @fires SearchBox#search - triggered when user clicks search icon or presses 'Enter' key
 * @fires SearchBox#change:query - triggered when input box value changes
 * @tutorial default
 */
export class SearchBox extends Backbone.View {
  /**
   * Intializes the view based on the options specified.
   * @param {Object} options - See {@link SearchBox#constructor} for more details
   * @private
   * @return {void}
   */
  initialize({
    maxLength = 150,
    placeholder = '',
    value = '',
    isDisabled = false,
    iconClasses = [],
    ariaLabel = null,
  } = {}) {
    this.maxLength = maxLength;
    this.placeholder = placeholder;
    this.iconClasses = iconClasses;
    this.ariaLabel = ariaLabel;

    this.viewModel = new ViewModel({
      value,
      isDisabled,
    });

    this.viewModel.value.subscribe((newValue) => {
      this.trigger('change:query', newValue);
    });
  }

  get events() {
    return {
      'keypress .search-box__input': onKeyPress,
      'click .search-box__icon': onClick,
    };
  }

  render() {
    this.$el.html(template({
      maxLength: this.maxLength,
      placeholder: this.placeholder,
      iconClasses: this.iconClasses,
      ariaLabel: this.ariaLabel || this.placeholder,
    }));

    ko.applyBindings(this.viewModel, this.el);

    return this;
  }

  /**
   * Enable the search input box
   * @return {void}
   */
  enable() {
    this.viewModel.isDisabled(false);
  }

  /**
   * Disable the search input box
   * @return {void}
   */
  disable() {
    this.viewModel.isDisabled(true);
  }
}
